import {
    Card,
    IconButton,
    Theme,
    Typography,
    makeStyles,
} from '@material-ui/core'
import Popper from '@material-ui/core/Popper'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { dragIconColor } from 'bpm/shared/bpmColors'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import ProductHistoryModal from './ProductHistoryModal/index'
import rowActionButtonStore, {
    RowActionButtonStoreType,
} from './RowActionButton.store'

const useStyles = makeStyles((theme: Theme) => ({
    popperWrapper: {
        zIndex: 1000,
        marginRight: theme.spacing(2),
    },
    actionsWrapper: {
        backgroundColor: theme.palette.common.white,
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1),
        zIndex: 1000,
    },
    productHistoryButton: {
        width: theme.spacing(17),
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: dragIconColor,
            cursor: 'pointer',
        },
    },
    rowActionButton: {
        position: 'absolute',
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: 'unset',
        },
        '&.disabled': {
            color: `${dragIconColor} !important`,
        },
    },
}))

type RowActionButtonProps = {
    cell: any
    isAdminAccess?: boolean
}

const RowActionButton: FC<RowActionButtonProps> = ({ cell, isAdminAccess }) => {
    const classes = useStyles()

    const activeRowAction = rowActionButtonStore(
        (state) => state.activeRowAction
    )

    const setActiveRowAction = rowActionButtonStore(
        (state: RowActionButtonStoreType) => state.setActiveRowAction
    )

    const isOpen = useMemo(() => {
        return activeRowAction === cell.row.original?.listingId
    }, [activeRowAction])

    const [productHistoryModalOpen, setProductHistoryModalOpen] = useState(
        false
    )

    const buttonRef = useRef(null)
    const popperRef = useRef(null)

    const onRowActionsClick = useCallback(
        (e) => {
            if (
                processEnv.REACT_APP_ShowProductHistoryButton !== '1' ||
                !isAdminAccess
            ) {
                return
            }

            e.preventDefault()
            e.stopPropagation()

            if (activeRowAction === cell.row.original?.listingId) {
                setActiveRowAction('')
            } else {
                setActiveRowAction(cell.row.original?.listingId)
            }
        },
        [
            setActiveRowAction,
            cell.row.original?.listingId,
            activeRowAction,
            isAdminAccess,
        ]
    )

    const handleProductHistoryClick = useCallback(() => {
        setProductHistoryModalOpen(true)
    }, [])

    const handleProductHistoryModalClose = useCallback(() => {
        setProductHistoryModalOpen(false)
        setActiveRowAction('')
    }, [setActiveRowAction])

    return (
        <>
            <IconButton
                onClick={onRowActionsClick}
                data-testid="btnRowAction"
                ref={buttonRef}
                className={classes.rowActionButton}
                style={{
                    height: '100%',
                }}
                aria-label="Actions"
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>

            <Popper
                open={isOpen}
                placement={'bottom-end'}
                anchorEl={buttonRef?.current}
                ref={popperRef}
                className={classes.popperWrapper}
            >
                <Card className={classes.actionsWrapper}>
                    <Typography
                        data-testid="tvProductHistoryButton"
                        component="div"
                        className={classes.productHistoryButton}
                        onClick={handleProductHistoryClick}
                    >
                        Product History
                    </Typography>
                </Card>
            </Popper>

            <ProductHistoryModal
                open={productHistoryModalOpen}
                listing={cell.row.original}
                onClose={handleProductHistoryModalClose}
            />
        </>
    )
}

export default RowActionButton
